import { ArrowLeftIcon } from '@phosphor-icons/react';
import React from 'react';

import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { InspectorBreadcrumb } from '@/components/ui/inspector/bodies/InspectorBreadcrumb';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { TEXT_FADE_RIGHT } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface InspectorHeaderProps {
  /** Fallback title shown when no breadcrumbs (and no backToProject) are supplied. */
  title: string;
  /** Root + leaf breadcrumb trail (e.g. "Project Name › Post"). */
  breadcrumbs?: InspectorBreadcrumbItem[];
  /** Back/close callback wired to the Esc / ← / overlay-click path. */
  onClose: () => void;
  /**
   * When set, the ← button returns to the parent project instead of closing
   * the inspector, and the centered title reads "Project Name › title".
   */
  backToProject?: { id: string; name: string; onReturn: () => void };
  /**
   * Non-icon status text rendered before the action pill (e.g. "Coming
   * soon", a "Saved" chip) — kept OUTSIDE the pill, which is icon-only.
   */
  meta?: React.ReactNode;
  /**
   * Right-slot icon actions — at most 2, fused into one glassy pill on
   * mobile and un-fused at desktop. Leave unset for an empty right slot;
   * don't invent actions to fill it.
   */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Canonical Inspector header — Header Law shape (2026-07-03): a glassy ←
 * circle on the left (visible at every breakpoint, since Inspector dialogs
 * have no sidebar/breadcrumb fallback for dismissal), a centered/truncated
 * title on mobile that reverts to left-aligned+static on desktop, and an
 * optional ≤2-icon action pill on the right. Mirrors
 * `MediaStudioDialog.tsx`'s header structure exactly.
 */
export const InspectorHeader: React.FC<InspectorHeaderProps> = ({
  title,
  breadcrumbs,
  onClose,
  backToProject,
  meta,
  actions,
  className,
}) => {
  const effectiveBreadcrumbs: InspectorBreadcrumbItem[] | undefined = breadcrumbs?.length
    ? breadcrumbs
    : backToProject
      ? [{ label: backToProject.name }]
      : undefined;

  return (
    <div className={cn('relative flex h-12 shrink-0 items-center gap-2 px-4', className)}>
      <div className="z-10 flex shrink-0 items-center">
        <CircleIconButton
          size="header"
          ariaLabel={backToProject ? `Back to ${backToProject.name}` : 'Back'}
          className="header-nav-btn rounded-full"
          onClick={backToProject ? backToProject.onReturn : onClose}
        >
          <ArrowLeftIcon weight="regular" />
        </CircleIconButton>
      </div>

      <div className="page-title-centered pointer-events-none min-w-0 md:flex md:flex-1 md:justify-start">
        {effectiveBreadcrumbs?.length ? (
          <div className="pointer-events-auto">
            <InspectorBreadcrumb breadcrumbs={effectiveBreadcrumbs} fallbackTitle={title} />
          </div>
        ) : (
          <h2
            className={cn(
              'pointer-events-auto text-sm font-medium text-foreground/90',
              TEXT_FADE_RIGHT,
            )}
          >
            {title}
          </h2>
        )}
      </div>

      {meta || actions ? (
        <div className="z-10 ml-auto flex shrink-0 items-center gap-2">
          {meta}
          {actions ? (
            <div className="header-action-pill inline-flex items-center gap-1 md:gap-1.5">
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
